import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Testimonial.css';

const testimonials = [
  {
    id: 1,
    name: "Maxwell Mutunga",
    role: "Client",
    image: "/images/testimonial/testimonial-1.jpg",
    rating: 5,
    text: "Musa Electrical did an amazing job installing our solar system. Professional, timely, and very knowledgeable. Our electricity bills have reduced significantly!"
  },
  {
    id: 2,
    name: "Benjamin Wambua",
    role: "Client",
    image: "/images/testimonial/testimonial-2.jpg",
    rating: 5,
    text: "We hired Musa Electrical for CCTV installation at our shop. The team was very professional and the system works perfectly. Highly recommended!"
  },
  {
    id: 3,
    name: "Francis Muli",
    role: "Client",
    image: "/images/testimonial/testimonial-3.jpg",
    rating: 4,
    text: "Excellent electrical design and installation for our apartment complex. They completed the project on time and within budget. Great workmanship!"
  },
  {
    id: 4,
    name: "Tom Nguza",
    role: "Client",
    image: "/images/testimonial/testimonial-4.jpg",
    rating: 5,
    text: "Fast and reliable electronics repair service. They fixed my TV and home theater system quickly. Very affordable prices too!"
  },
  {
    id: 5,
    name: "Abraham Kivondo",
    role: "Client",
    image: "/images/testimonial/testimonial-5.jpg",
    rating: 5,
    text: "Fast and reliable electronics repair service. They fixed my TV and home theater system quickly. Very affordable prices and great customer service!"
  }
];

const Testimonial = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const trackRef = useRef(null);
  const totalSlides = Math.ceil(testimonials.length / 2);
  const maxSlide = totalSlides - 1;

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
  }, [maxSlide]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
  }, [maxSlide]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
    setIsPaused(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const offset = e.clientX - dragStart;
    setDragOffset(offset);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    setIsPaused(false);
    if (dragOffset > 50) prevSlide();
    else if (dragOffset < -50) nextSlide();
    setDragOffset(0);
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      setIsPaused(false);
      if (dragOffset > 50) prevSlide();
      else if (dragOffset < -50) nextSlide();
      setDragOffset(0);
    }
  };

  const [touchStart, setTouchStart] = useState(0);
  const [touchOffset, setTouchOffset] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchOffset(0);
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    const offset = e.touches[0].clientX - touchStart;
    setTouchOffset(offset);
  };

  const handleTouchEnd = () => {
    setIsPaused(false);
    if (touchOffset > 50) prevSlide();
    else if (touchOffset < -50) nextSlide();
    setTouchOffset(0);
  };

  useEffect(() => {
    if (isPaused || isDragging) return;
    const interval = setInterval(nextSlide, 4000);
    return () => clearInterval(interval);
  }, [nextSlide, isPaused, isDragging]);

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaX > 30 || e.deltaY > 30) nextSlide();
    else if (e.deltaX < -30 || e.deltaY < -30) prevSlide();
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={`star ${i < rating ? 'filled' : ''}`}>★</span>
      );
    }
    return stars;
  };

  const getTransformStyle = () => {
    const baseTransform = currentSlide * 100;
    const dragPercentage = isDragging ? (dragOffset / (trackRef.current?.offsetWidth || 1)) * 100 : 0;
    return `translateX(-${baseTransform - dragPercentage}%)`;
  };

  return (
    <section className="testimonial-section">
      <div className="testimonial-container">
        <div className="testimonial-row">
          <div className="testimonial-left">
            <h4 className="testimonial-subtitle">Testimonial</h4>
            <h1 className="testimonial-title">Powerful Praise From Our Customers</h1>
            <p className="testimonial-description">
              We take pride in delivering exceptional electrical services.
              Here's what our satisfied clients have to say about their experience with us.
            </p>
            <a href="tel:0796049254" className="testimonial-cta">Contact Us Today</a>
          </div>

          <div className="testimonial-right">
            <div className="testimonial-carousel" onWheel={handleWheel}>
              <div
                className="carousel-viewport"
                ref={trackRef}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <div
                  className="carousel-track"
                  style={{
                    transform: getTransformStyle(),
                    transition: isDragging ? 'none' : 'transform 0.5s ease'
                  }}
                >
                  {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                    <div className="carousel-slide" key={slideIndex}>
                      {testimonials.slice(slideIndex * 2, slideIndex * 2 + 2).map((testimonial) => (
                        <div className="testimonial-item" key={testimonial.id}>
                          <div className="testimonial-quote">❝</div>
                          <div className="testimonial-inner">
                            <img
                              src={process.env.PUBLIC_URL + testimonial.image}
                              alt={testimonial.name}
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" fill="%231a1a2e"><rect width="100" height="100"/><text x="50%" y="50%" fill="%23ffd700" font-size="14" text-anchor="middle" dy=".3em">' + testimonial.name.charAt(0) + '</text></svg>';
                              }}
                            />
                            <div className="testimonial-info">
                              <h4>{testimonial.name}</h4>
                              <p>{testimonial.role}</p>
                              <div className="testimonial-stars">
                                {renderStars(testimonial.rating)}
                              </div>
                            </div>
                          </div>
                          <div className="customer-text">
                            <p>{testimonial.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="testimonial-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`t-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                >
                  <span></span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonial;
